import { RRule, rrulestr } from "rrule";
import { frequencyPresets } from "../../constants/frequencyPresets";

export const formatRruleDays = (rrule, t) => {
  if (!rrule) {
    return '';
  }

  try {
    const daysMap = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
    const daysNames = {
      MO: t("rrules.byweekday.MO"),
      TU: t("rrules.byweekday.TU"),
      WE: t("rrules.byweekday.WE"),
      TH: t("rrules.byweekday.TH"),
      FR: t("rrules.byweekday.FR"),
      SA: t("rrules.byweekday.SA"),
      SU: t("rrules.byweekday.SU"),
    };
    const rruleObject = rrulestr(rrule);

    if (!rruleObject.origOptions.byweekday) {
      return '';
    }

    const selectedDays = rruleObject.origOptions.byweekday.map((weekday) => daysMap[weekday?.weekday || weekday]);
    return selectedDays.map(day => daysNames[day]).join(", ");
  } catch (error) {
    console.error("Invalid rrule format", error);
    return '';
  }
};

export const getDays = (ruleString) => {
  const byDayMatch = ruleString.match(/BYDAY=([^;]+)/);

  if (byDayMatch) {
    const byDayString = byDayMatch[1] || "";
    return byDayString.split(",")
  }

  return [];
}

/**
 * Build a weekly RRULE string from day codes (["WE","TH"] -> FREQ=WEEKLY;BYDAY=WE,TH).
 * Shared so the day selector and the cells that seed it cannot drift apart.
 */
export const buildWeeklyRrule = (days = []) => {
  const weekdayOrder = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

  const valid = [...new Set(days.filter((day) => weekdayOrder.includes(day)))]
    .sort((left, right) => weekdayOrder.indexOf(left) - weekdayOrder.indexOf(right));

  if (!valid.length) {
    return null;
  }

  return new RRule({
    freq: RRule.WEEKLY,
    byweekday: valid.map((day) => RRule[day]),
  }).toString();
};

export const formatDaysArray = (days = [], t) => {
  const validDays = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
  return days
    .filter((day) => validDays.includes(day))
    .map((day) => t(`rrules.byweekday.${day}`));
};

export const getTheNameOfFrequency = (record) => {
  const frequencies = frequencyPresets.map(({ translationKey, rrule_blueprint }) => ({
    name: translationKey,
    rule: rrule_blueprint,
  }));

  function parseRRule(rrule) {
    if (typeof rrule !== 'string') return {};
    if (rrule.startsWith('RRULE:')) {
      rrule = rrule.slice(6);
    }
    return rrule.split(';').reduce((acc, part) => {
      const [key, value] = part.split('=');
      acc[key] = value;
      return acc;
    }, {});
  }

  function normalizeValue(value) {
    return value.split(',').map(v => v.trim()).sort().join(',');
  }

  function getCount(value) {
    return value.split(',').length;
  }

  function getCountDays(inputParts) {
    const inputBYDAY = inputParts.BYDAY ? normalizeValue(inputParts.BYDAY) : null;
    const inputBYMONTH = inputParts.BYMONTH ? normalizeValue(inputParts.BYMONTH) : null;
    const inputBYMONTHDAY = inputParts.BYMONTHDAY ? normalizeValue(inputParts.BYMONTHDAY) : null;
    return inputBYDAY ? getCount(inputBYDAY) : inputBYMONTH ? getCount(inputBYMONTH) : inputBYMONTHDAY ? getCount(inputBYMONTHDAY) : 0;
  }

  const cleaningDaysCount =
    record.building_unit.cleaning_days.length === 1 && record.building_unit.cleaning_days[0].includes(',')
      ? record.building_unit.cleaning_days[0].split(',').length
      : record.building_unit.cleaning_days.length;

  let inputRRule = record.rrule
  let inputParts = parseRRule(inputRRule);
  let inputDayCount = getCountDays(inputParts);

  if (cleaningDaysCount && cleaningDaysCount !== inputDayCount) {
    inputDayCount = cleaningDaysCount;

    const serviceRrules = record?.building_unit?.service_rrules || [];

    for (const item of serviceRrules) {
      const rruleParts = parseRRule(item.rrule);
      const rruleDayCount = getCountDays(rruleParts);

      if (
        rruleParts.FREQ === inputParts.FREQ &&
        inputDayCount === rruleDayCount
      ) {
        inputParts = rruleParts
        break;
      }
    }
  }

  const inputFREQ = inputParts.FREQ;
  const inputInterval = inputParts.INTERVAL || "1";

  for (const freq of frequencies) {
    const freqParts = parseRRule(freq.rule);

    const freqFREQ = freqParts.FREQ;
    const freqInterval = freqParts.INTERVAL || "1";
    const freqBYDAY = freqParts.BYDAY ? normalizeValue(freqParts.BYDAY) : null;
    const freqBYMONTH = freqParts.BYMONTH ? normalizeValue(freqParts.BYMONTH) : null;
    const freqBYMONTHDAY = freqParts.BYMONTHDAY ? normalizeValue(freqParts.BYMONTHDAY) : null;
    const freqDayCount = freqBYDAY ? getCount(freqBYDAY) : freqBYMONTH ? getCount(freqBYMONTH) : freqBYMONTHDAY ? getCount(freqBYMONTHDAY) : 0;

    const sameFreq = inputFREQ === freqFREQ;
    const sameInterval = inputInterval === freqInterval;
    const sameDayCount = inputDayCount === freqDayCount;

    if (sameFreq && sameInterval && sameDayCount) {
      return freq.name;
    }
  }

  return null;
}
